import { Dropdown } from "react-bootstrap";
import Button from "app/storybookComponents/Button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useAppSelector } from "utils/redux/hooks";
import { useMemo, useState } from "react";
import Paginator from "app/components/SortableTable/Paginator";
import CollapsibleCard from "app/components/AnonymousFeedback/CollapsibleCard";
import Loading from "app/storybookComponents/Loading";
import {
  selectTeamScanFeedback,
  selectTextSynthesis,
  selectGetTextInsightsStatus,
} from "../slice";
import { RawTextFeedback, TextInsightResponse } from "../types";
import SimpleModal from "app/components/Modals/SimpleModal";
import { downloadCSV } from "utils/helperFunctions";
import { faDownload } from "@fortawesome/pro-regular-svg-icons";

interface Props {
  show: boolean;
  textInsights: TextInsightResponse | null;
  entityType: "department" | "organization" | "team";
  onHide: () => void;
  title?: string;
  aiFeaturesEnabled?: boolean;
  showAIThemes?: boolean;
  setShowAIThemes?: (value: boolean) => void;
  hideDropdown?: boolean;
}

const MAX_FEEDBACK_PER_PAGE = 3;

export default function FreeTextInsightsModal({
  show,
  textInsights,
  entityType,
  onHide,
  title = "Free Text Insights",
  aiFeaturesEnabled,
  showAIThemes,
  setShowAIThemes,
  hideDropdown,
}: Readonly<Props>) {
  const textSynthesis = useAppSelector(selectTextSynthesis);
  const teamScanFeedback = useAppSelector(selectTeamScanFeedback);
  const getTextInsightsStatus = useAppSelector(selectGetTextInsightsStatus);
  const [pageShowingMap, setPageShowingMap] = useState<{
    doingWell: number;
    couldImproveOn: number;
  }>({
    doingWell: 1,
    couldImproveOn: 1,
  });

  const themeText = useMemo(() => {
    if (typeof textSynthesis?.themeText === "string") {
      return textSynthesis?.themeText;
    }
    return null;
  }, [textSynthesis?.themeText]);

  const skillsText = useMemo(() => {
    if (typeof textSynthesis?.skillsText === "string") {
      return textSynthesis?.skillsText;
    }
    return null;
  }, [textSynthesis?.skillsText]);

  const getSnapShot = () => {
    if (!textSynthesis || !aiFeaturesEnabled || (!themeText && !skillsText)) {
      return null;
    }
    return (
      <div className="snapshot-box">
        <FontAwesomeIcon icon={["far", "lightbulb-on"]} />
        <div className="column-gap-8px">
          <p>Synthesis: {themeText}</p>
          <p>{skillsText}</p>
        </div>
      </div>
    );
  };

  const getAuthor = (textInsightResponses: RawTextFeedback) => {
    const date = new Date(textInsightResponses.dateCompleted);
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const year = date.getFullYear().toString().slice(-2);
    const dateString = textInsightResponses.dateCompleted
      ? `(from ${month}/${day}/${year})`
      : "";
    if (entityType !== "organization") {
      return `from Team member ${dateString}`;
    }

    if (!textInsightResponses.departmentName) {
      return `Team member from your organization ${dateString}`;
    }

    return `Team member from the ${textInsightResponses.departmentName} ${dateString}`;
  };

  const getAnonymousFeedbackBox = (textInsightResponses: RawTextFeedback) => {
    return (
      <div className="row-gap-12px">
        <div className="anonymous-user">
          <FontAwesomeIcon icon={["fas", "user"]} />
        </div>
        <div>
          <p className="quote">"{textInsightResponses.response}"</p>
          <p className="author">{getAuthor(textInsightResponses)}</p>
        </div>
      </div>
    );
  };

  const getPaginator = (key: "couldImproveOn" | "doingWell", arr: any[]) => {
    if (arr.length <= MAX_FEEDBACK_PER_PAGE) {
      return null;
    }

    return (
      <Paginator
        pageCount={Math.ceil(arr.length / MAX_FEEDBACK_PER_PAGE)}
        onPageChange={(page) =>
          setPageShowingMap((prevState) => ({
            ...prevState,
            [key]: page.selected + 1,
          }))
        }
        className="justify-content-start"
      />
    );
  };

  const getFeedbackShowing = (
    key: "couldImproveOn" | "doingWell",
    textInsightResponses: RawTextFeedback[]
  ) => {
    const currentPage = pageShowingMap[key];
    const startIndex = (currentPage - 1) * MAX_FEEDBACK_PER_PAGE;
    const endIndex = currentPage * MAX_FEEDBACK_PER_PAGE;
    return textInsightResponses
      .slice(startIndex, endIndex)
      .map(getAnonymousFeedbackBox);
  };

  const getSortedFeedbackArray = (arr: RawTextFeedback[]) => {
    return [...arr].sort((b, a) => {
      if (!a.dateCompleted || !b.dateCompleted) {
        return 0;
      }
      if (!a.dateCompleted) {
        return 1;
      }
      if (!b.dateCompleted) {
        return -1;
      }
      const dateA = new Date(a.dateCompleted);
      const dateB = new Date(b.dateCompleted);
      return dateA.getTime() - dateB.getTime();
    });
  };

  const getDoingWell = () => {
    const doingWellArr = getSortedFeedbackArray(
      teamScanFeedback?.doingWell ?? []
    );

    return (
      <div className="column-gap-20px">
        <h4>
          {entityType === "team"
            ? "What members think this team is"
            : "What members think their teams are"}{" "}
          <span style={{ color: "#425CC7" }}>doing well:</span>
        </h4>
        {getFeedbackShowing("doingWell", doingWellArr)}
        {getPaginator("doingWell", doingWellArr)}
      </div>
    );
  };

  const getCouldBeBetter = () => {
    const couldImproveOnArr = getSortedFeedbackArray(
      teamScanFeedback?.couldImproveOn ?? []
    );
    return (
      <div className="column-gap-20px">
        <h4>
          {entityType === "team"
            ? "What members think this team could be"
            : "What members think their teams could be"}{" "}
          <span style={{ color: "#425CC7" }}>doing better:</span>
        </h4>
        {getFeedbackShowing("couldImproveOn", couldImproveOnArr)}
        {getPaginator("couldImproveOn", couldImproveOnArr)}
      </div>
    );
  };

  const getDoingWellWithTheme = () => {
    if (!textInsights) {
      return null;
    }
    return (
      <div className="column-gap-20px">
        <h4>
          Based on {textInsights.responsesCount} survey responses, here are the
          top themes that emerged about what this {entityType} is doing well:
        </h4>
        <div className="collapsible-cards-container">
          {textInsights?.doingWell?.map((item, index) => {
            return (
              <CollapsibleCard
                key={index}
                title={item.theme}
                feedbackArr={[
                  {
                    feedbackText: item.response,
                    feedbackDate: item.responseInfo?.dateResponseGiven,
                    from: item.responseInfo?.department
                      ? item.responseInfo.department
                      : "No Department",
                    type: "Department",
                  },
                ]}
              />
            );
          })}
        </div>
      </div>
    );
  };

  const getCouldBeBetterWithTheme = () => {
    if (!textInsights) {
      return null;
    }
    return (
      <div className="column-gap-20px">
        <h4>
          Based on {textInsights.responsesCount} survey responses, here are the
          top themes that emerged about what this {entityType} could be doing
          better:
        </h4>
        <div className="collapsible-cards-container">
          {textInsights?.couldImproveOn?.map((item, index) => {
            return (
              <CollapsibleCard
                key={index}
                title={item.theme}
                feedbackArr={[
                  {
                    feedbackText: item.response,
                    feedbackDate: item.responseInfo?.dateResponseGiven,
                    from: item.responseInfo?.department
                      ? item.responseInfo.department
                      : "No Department",
                    type: "Department",
                  },
                ]}
              />
            );
          })}
        </div>
      </div>
    );
  };

  const getBody = () => {
    if (getTextInsightsStatus === "loading") {
      return <Loading />;
    }

    if (showAIThemes) {
      return (
        <>
          {getDoingWellWithTheme()}
          <hr className="m-0" />
          {getCouldBeBetterWithTheme()}
        </>
      );
    }
    return (
      <>
        {getDoingWell()}
        <hr className="m-0" />
        {getCouldBeBetter()}
      </>
    );
  };

  const getDropdownSection = () => {
    if (!aiFeaturesEnabled || hideDropdown) {
      return null;
    }
    const selectedValue = showAIThemes ? "AI Generated Themes" : "All Comments";
    return (
      <div className="column-gap-20px">
        <div className="flex justify-between">
          <Dropdown
            onSelect={(eventKey) => {
              if (eventKey === "1") {
                setShowAIThemes?.(false);
              } else {
                setShowAIThemes?.(true);
              }
            }}
          >
            <Dropdown.Toggle variant="light" id="dropdown-basic">
              {selectedValue}
            </Dropdown.Toggle>

            <Dropdown.Menu>
              <Dropdown.Item eventKey="1">All Comments</Dropdown.Item>
              <Dropdown.Divider />
              <Dropdown.Item eventKey="2">AI Generated Themes</Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
          {!showAIThemes && (
            <Button
              onClick={onDownloadCSV}
              variant="secondary-blue"
              disabled={
                !teamScanFeedback?.couldImproveOn?.length &&
                !teamScanFeedback?.doingWell?.length
              }
            >
              Export CSV
              <FontAwesomeIcon icon={faDownload} className="ms-2" />
            </Button>
          )}
        </div>
        <hr className="m-0" />
      </div>
    );
  };

  const onDownloadCSV = () => {
    if (showAIThemes) {
      return;
    }
    const csvData: string[][] = [
      ["Category", "Response", "Department", "Date"],
    ];

    const categories = [
      { label: "Doing Well", data: teamScanFeedback?.doingWell ?? [] },
      {
        label: "Could Improve On",
        data: teamScanFeedback?.couldImproveOn ?? [],
      },
    ];

    for (const { label, data } of categories) {
      for (const item of data) {
        csvData.push([
          label,
          item.response,
          item.departmentName ?? "",
          item.dateCompleted ?? "",
        ]);
      }
    }

    downloadCSV(`${entityType} Feedback Responses`, csvData);
  };

  return (
    <SimpleModal
      title={title}
      onHide={onHide}
      show={show}
      className="free-text-insights-modal"
    >
      {getSnapShot()}
      {getDropdownSection()}
      {getBody()}
    </SimpleModal>
  );
}
